import React, { useState } from 'react';
import { FaEnvelope, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import './SubscribeForm.css';

const GENERIC_ERROR = 'Could not subscribe. Please try again.';

// The dev server proxies unknown paths to WordPress, so /api/subscribe returns
// an HTML 404 in local dev. Read the body defensively so that surfaces as a
// readable message instead of a JSON parse error.
const readResponse = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return {
      message: res.ok
        ? GENERIC_ERROR
        : `Subscribe service unavailable (${res.status}). Serverless functions only run on Vercel — use \`vercel dev\` locally.`,
    };
  }
};

const SubscribeForm = ({ variant = 'post' }) => {
  const [email, setEmail]   = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [outcome, setOutcome]   = useState(null); // { already, pending }

  const inputId = `subscribe-email-${variant}`;
  const loading = status === 'loading';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await readResponse(res);
      if (!res.ok) throw new Error(data.message || GENERIC_ERROR);
      setOutcome({ already: Boolean(data.already), pending: Boolean(data.pending) });
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || GENERIC_ERROR);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className={`subscribe-wrap subscribe-wrap--${variant} subscribe-wrap--success`} role="status">
        <FaCheckCircle className="subscribe-success-icon" aria-hidden="true" />
        <p className="subscribe-success-msg">
          {outcome?.already
            ? "You're already on the list — nothing more to do."
            : outcome?.pending
              ? 'Almost there — check your inbox and click the confirmation link.'
              : "You're subscribed! New posts will land in your inbox."}
        </p>
      </div>
    );
  }

  return (
    <div className={`subscribe-wrap subscribe-wrap--${variant}`}>
      <div className="subscribe-header">
        <FaEnvelope className="subscribe-icon" aria-hidden="true" />
        <div>
          <h3 className="subscribe-title">Get new posts by email</h3>
          <p className="subscribe-subtitle">No spam — just dive reports when they're published.</p>
        </div>
      </div>
      <form className="subscribe-fields" onSubmit={handleSubmit}>
        <label className="subscribe-label" htmlFor={inputId}>Email address</label>
        <input
          id={inputId}
          type="email"
          className="subscribe-input"
          placeholder="your@email.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={loading}
          required
        />
        <button type="submit" className="subscribe-btn" disabled={loading}>
          {loading ? <FaSpinner className="subscribe-spinner" aria-label="Subscribing" /> : 'Subscribe'}
        </button>
      </form>
      {status === 'error' && <p className="subscribe-error" role="alert">{errorMsg}</p>}
    </div>
  );
};

export default SubscribeForm;
